/**
=========================================================
* Material Dashboard 2 React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useMemo } from "react";

// react-router components
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Material Dashboard 2 React example components
import Configurator from "examples/Configurator";

// Material Dashboard 2 React themes
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";

// Material Dashboard 2 React Dark Mode themes
import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";

// RTL plugins
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

// Material Dashboard 2 React routes
import routes from "routes";

// Material Dashboard 2 React contexts
import { useMaterialUIController, GlobalMDAlertProvider } from "context";

// Images
import Detalleexpediente from "modules/prestamos/detalleexpediente";
import DetalleIncauto from "modules/motores/incautos";
import DetalleQrexpediente from "modules/prestamos/qrexpediente";
import Box from "@mui/material/Box";
import SimpleSidenav from "modules/menu/SimpleSidenav";
import ModulosCliente from "modules/menu/PantallaModulosCliente";

export default function App() {
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    openConfigurator,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();
  const pathnameSegments = pathname.split("/").filter(Boolean);
  const isDetalleQrexpedienteRoute =
    pathnameSegments.length === 3 && !["expediente", "incauto"].includes(pathnameSegments[0]);

  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });

    setRtlCache(cacheRtl);
  }, []);


  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        return <Route exact  path={route.route} element={route.component} key={route.key} />;
      }

      return null;
  });

  return direction === "rtl" ? (
    <CacheProvider value={rtlCache}>
      <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>
        <CssBaseline />
      </ThemeProvider>
    </CacheProvider>
  ) : (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />
      <GlobalMDAlertProvider>
        <Box sx={{ display: "flex", minHeight: "100vh" }}>
          {pathname !== "/login" &&
            pathname !== "/moduloscliente" &&
            !isDetalleQrexpedienteRoute && <SimpleSidenav routes={routes} />}
        
          <Box sx={{ flex: 1 }}>
            <Routes>
              {getRoutes(routes)}
              <Route 
                exact
                path="/moduloscliente" 
                element={<ModulosCliente/>} 
                key="moduloscliente" 
               />
              <Route
                exact
                path={"/:nombrenegocio/:id/:data"}
                element={<DetalleQrexpediente />}
                key="detalleQrexpedientes"
              />
              <Route
                exact
                path={"/expediente/:id/:data"}
                element={<Detalleexpediente />}
                key="detalleexpedientes"
              />
              
              <Route exact path={"/incauto/:id/:data"} element={<DetalleIncauto />} key="detalleincauto" />
              <Route path="*" element={<Navigate to="/login" />} />
              
            </Routes>
          </Box>
        </Box>
      </GlobalMDAlertProvider>
    </ThemeProvider>
  );
}
